// --- START OF FILE GameContainer.js ---

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { 
    saveMatch, 
    getMatchHistory, 
    getAllUserProfiles,
    updateUserProfile,
    createMatch,
    updateMatch,
    getActiveMatchForUser,
    listenForInvites 
} from '../services/firestoreService';
import { Check, Mail, X } from 'lucide-react'; 
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    getAvailableBalls, 
    getWinningBall,
    checkWinCondition,
    checkSpecialShots
} from '../utils/gameConfig';

import { HomeView } from './views/HomeView';
import { SetupView } from './views/SetupView';
import { TrackingView } from './views/TrackingView';
import { SimpleTrackingView } from './views/SimpleTrackingView';
import { CompletedView } from './views/CompletedView';
import { AnalyticsView } from './views/AnalyticsView';
import { StatsView } from './views/StatsView';
import { ProfileView } from './views/ProfileView';
import { WaitingView } from './views/WaitingView';
import { PracticeView } from './views/PracticeView';
import { DrillView } from './views/DrillView';

const defaultSetupData = {
    player1: 'Player 1',
    player1Id: '',
    player2: 'Player 2',
    player2Id: '',
    player1IsProfile: true,
    player2IsProfile: false,
    gameType: '9-Ball',
    player1RaceTo: 7,
    player2RaceTo: 7,
    location: '',
    whoBreaks: 'winner',
    firstBreaker: 'player1',
    threeFoulRule: true,
    nineBallRule: 'wild',
    wildNineOnBreak: true,
    tenBallLast: false,
    callEight: false,        // 8-Ball specific: Call 8-Ball
    eightOnBreakLoss: false, // 8-Ball specific: 8-Ball on Break is Loss
    rackSetup: 'standard',
    controlMode: 'strict',
    gameMode: 'pvp',
    trackingMode: 'detailed',
};

const defaultGameState = {
    currentPlayerId: null,
    currentInning: 1,
    currentShot: 1,
    player1Score: 0,
    player2Score: 0,
    consecutiveFouls: {},
    winner: null,
    startTime: null,
    endTime: null,
    isPushOutDecision: false,
    ballInHand: false,
    currentShotStartTime: null,
    ballsRemaining: [],
    historyStack: [],
    // Timeout state
    isTimeout: false,
    timeoutRequestedBy: null,
    timeoutStartTime: null,
    timeoutDuration: 0, // in seconds
};

const defaultShotData = { result: '', tags: [], ballsMade: [], distance: '', notes: '' };

const ChallengeNotificationModal = ({ invite, onAccept, onDecline }) => {
    if (!invite) return null;

    const challengerName = invite.matchData.player1;

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm animate-fade-in">
            <div className="bg-slate-800 rounded-2xl p-6 max-w-sm w-full mx-4 border-2 border-purple-500 shadow-2xl animate-slide-up">
                <div className="text-center">
                    <div className="bg-purple-500/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 border-2 border-purple-500">
                        <Mail className="w-8 h-8 text-purple-400" />
                    </div>
                    <h2 className="text-2xl font-bold mb-2">New Challenge!</h2>
                    <p className="text-slate-300 mb-4">
                        <span className="font-bold text-white">{challengerName}</span> has challenged you to a game of {invite.matchData.gameType}.
                    </p>
                    <div className="bg-slate-700/50 rounded-lg p-3 text-sm text-slate-400">
                        Race to {invite.matchData.player1RaceTo} - {invite.matchData.player2RaceTo}
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-6">
                    <button 
                        onClick={() => onDecline(invite.id)} 
                        className="w-full bg-red-600/80 text-white py-3 rounded-lg font-semibold hover:bg-red-600 transition-all flex items-center justify-center gap-2"
                    >
                        <X size={20} /> Decline
                    </button>
                    <button 
                        onClick={() => onAccept(invite.id)} 
                        className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-all flex items-center justify-center gap-2"
                    >
                        <Check size={20} /> Accept
                    </button>
                </div>
            </div>
        </div>
    );
};


export const GameContainer = () => {
    const { currentUser, currentUserData, logout, fullDeleteAccount, refreshUserData } = useAuth();
    
    const [currentView, setCurrentView] = useState('home');
    const [previousView, setPreviousView] = useState('home');
    const [activeMatch, setActiveMatch] = useState(null);
    const [setupMatchData, setSetupMatchData] = useState(defaultSetupData);
    const [opponentProfile, setOpponentProfile] = useState(null);
    const [guestOpponentName, setGuestOpponentName] = useState('');
    const [currentShotData, setCurrentShotData] = useState(defaultShotData);

    const [matchHistory, setMatchHistory] = useState([]);
    const [invites, setInvites] = useState([]);
    const [allProfiles, setAllProfiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [reviewingMatch, setReviewingMatch] = useState(null);
    const [initialLoading, setInitialLoading] = useState(true);
    const currentViewRef = useRef(currentView);
    useEffect(() => {
        currentViewRef.current = currentView;
    }, [currentView]);


    useEffect(() => {
        const checkForActiveMatch = async () => {
            if (currentUser) {
                const match = await getActiveMatchForUser(currentUser.uid);
                if (match) {
                    setActiveMatch(match);
                    navigateTo('tracking');
                }
            }
            setInitialLoading(false);
        };

        checkForActiveMatch();
    }, [currentUser]);

    useEffect(() => {
        if (currentUser) {
            const unsubscribe = listenForInvites(currentUser.uid, (newInvites) => {
                setInvites(newInvites);
            });
            return () => unsubscribe();
        }
    }, [currentUser]);

    useEffect(() => {
        if (!activeMatch?.id || activeMatch.isLocal) return;

        const matchDocRef = doc(db, 'matches', activeMatch.id);
        const unsubscribe = onSnapshot(matchDocRef, async (doc) => {
            const currentViewValue = currentViewRef.current;

            if (doc.exists()) {
                const match = { id: doc.id, ...doc.data() };
                const opponentName = match.matchData.player1Id === currentUser.uid ? match.matchData.player2 : match.matchData.player1;

                setActiveMatch(prev => ({ ...prev, ...match }));

                if (match.status === 'active' && currentViewValue !== 'tracking' && currentViewValue !== 'analytics') {
                    navigateTo('tracking');
                } else if (match.status === 'declined') {
                    if (currentViewValue === 'waiting') {
                        toast.error(`${opponentName} declined the challenge.`);
                        setActiveMatch(null);
                        navigateTo('home');
                    }
                } else if (match.status === 'cancelled') {
                     if (currentViewValue === 'waiting') {
                        toast.error("The match challenge was cancelled by the creator.");
                        setActiveMatch(null);
                        navigateTo('home');
                    }
                } else if (match.gameState.winner && currentViewValue !== 'completed' && currentViewValue !== 'analytics') {
                    navigateTo('completed');
                }
            } else {
                 if (currentViewValue === 'waiting') {
                    toast.error("The match challenge was cancelled or deleted.");
                    setActiveMatch(null);
                    navigateTo('home');
                 }
            }
        });

        return () => unsubscribe();
    }, [activeMatch?.id, activeMatch?.isLocal, currentUser?.uid]);

    const fetchHomeData = async () => {
        if (!currentUser) return;
        setLoading(true);
        const history = await getMatchHistory(currentUser.uid);
        const profiles = await getAllUserProfiles();
        setMatchHistory(history);
        setAllProfiles(profiles);
        setLoading(false);
    };

    useEffect(() => {
        if (currentView === 'home' && currentUser && !initialLoading) {
            fetchHomeData();
        }
    }, [currentView, currentUser, initialLoading]);
    
    const navigateTo = (view) => {
        setPreviousView(currentView);
        setCurrentView(view);
    };

    const startNewMatch = async () => {
        setActiveMatch(null);
        setReviewingMatch(null);
        setCurrentShotData(defaultShotData);

        setLoading(true);
        const profiles = await getAllUserProfiles();
        const opponentProfiles = profiles.filter(p => p.uid !== currentUser.uid);
        setAllProfiles(opponentProfiles);
        
        let initialSetupData = {
            ...defaultSetupData,
            player1: currentUserData.name,
            player1Id: currentUser.uid,
            player1IsProfile: true,
        };

        if (opponentProfiles.length > 0) {
            const firstOpponent = opponentProfiles[0];
            setOpponentProfile(firstOpponent);
            initialSetupData = {
                ...initialSetupData,
                player2: firstOpponent.name,
                player2Id: firstOpponent.uid,
                player2IsProfile: true,
            };
        } else {
            setGuestOpponentName('Guest');
             initialSetupData = {
                ...initialSetupData,
                player2: 'Guest',
                player2Id: null,
                player2IsProfile: false,
                controlMode: 'shared',
            };
        }
        setSetupMatchData(initialSetupData);
        setLoading(false);
        navigateTo('setup');
    };

    const handleSendChallenge = async () => {
        const isLocalMatch = setupMatchData.gameMode === 'ghost' || !opponentProfile;

        if (isLocalMatch) {
            const player2Name = setupMatchData.gameMode === 'ghost' ? 'Ghost' : setupMatchData.player2.trim();
            const player2Id = setupMatchData.gameMode === 'ghost' ? 'ghost' : `guest-${Date.now()}`;
            
            const localMatchData = {
                ...setupMatchData,
                player2: player2Name,
                player2Id,
                player2IsProfile: false,
                controlMode: 'shared',
                trackingMode: setupMatchData.trackingMode,
                playerIds: [currentUser.uid, player2Id]
            };
            const initialGameState = { 
                ...defaultGameState, 
                ballsRemaining: getAvailableBalls(localMatchData.gameType), 
                currentPlayerId: localMatchData.firstBreaker === 'player1' ? currentUser.uid : player2Id,
                startTime: new Date().toISOString(), 
                currentShotStartTime: new Date().toISOString()
            };
            setActiveMatch({ id: `local-${Date.now()}`, isLocal: true, matchData: localMatchData, gameState: initialGameState, shotLog: [] });
            navigateTo('tracking');
        } else {
            const finalMatchData = {
                gameMode: 'pvp',
                gameType: setupMatchData.gameType,
                location: setupMatchData.location,
                controlMode: setupMatchData.controlMode,
                trackingMode: setupMatchData.trackingMode,
                player1: currentUserData.name,
                player1Id: currentUser.uid,
                player1IsProfile: true,
                player1RaceTo: setupMatchData.player1RaceTo,
                player2: opponentProfile.name,
                player2Id: opponentProfile.uid,
                player2IsProfile: true,
                player2RaceTo: setupMatchData.player2RaceTo,
                whoBreaks: setupMatchData.whoBreaks,
                firstBreaker: setupMatchData.firstBreaker,
                threeFoulRule: setupMatchData.threeFoulRule,
                nineBallRule: setupMatchData.nineBallRule,
                wildNineOnBreak: setupMatchData.wildNineOnBreak,
                tenBallLast: setupMatchData.tenBallLast,
                rackSetup: setupMatchData.rackSetup,
                playerIds: [currentUser.uid, opponentProfile.uid],
                createdBy: currentUser.uid,
            };
            const initialGameState = { 
                ...defaultGameState, 
                ballsRemaining: getAvailableBalls(finalMatchData.gameType), 
                currentPlayerId: finalMatchData.firstBreaker === 'player1' ? finalMatchData.player1Id : finalMatchData.player2Id,
                consecutiveFouls: { [finalMatchData.player1Id]: 0, [finalMatchData.player2Id]: 0 }
            };
            try {
                const newMatchRef = await createMatch(finalMatchData, initialGameState);
                setActiveMatch({ id: newMatchRef.id, status: 'pending', matchData: finalMatchData, gameState: initialGameState, shotLog: [] });
                navigateTo('waiting');
            } catch (error) {
                console.error("Error creating match:", error);
                toast.error("Could not send challenge. Please try again.");
            }
        }
    };
    
    const handleCancelChallenge = async () => {
        if (!activeMatch?.id || activeMatch.isLocal) return;
        try {
            await updateMatch(activeMatch.id, { status: 'cancelled' }); 
            setActiveMatch(null);
            navigateTo('home');
        } catch (error) {
            console.error("Error cancelling challenge:", error);
            toast.error("Could not cancel challenge.");
        }
    };

    const handleAcceptChallenge = async (matchId) => {
        const acceptedMatchDoc = await getDoc(doc(db, 'matches', matchId));
        if (acceptedMatchDoc.exists()) {
            try {
                const matchData = { id: matchId, ...acceptedMatchDoc.data() };
                setActiveMatch(matchData);
                await updateMatch(matchId, { 
                    status: 'active',
                    'gameState.startTime': new Date().toISOString(),
                    'gameState.currentShotStartTime': new Date().toISOString()
                });
                navigateTo('tracking');
            } catch (error) {
                console.error("Error accepting challenge:", error);
                toast.error("Could not accept challenge.");
                setActiveMatch(null);
            }
        }
    };
    
    const handleDeclineChallenge = async (matchId) => {
        setInvites(prevInvites => prevInvites.filter(invite => invite.id !== matchId));
        await updateMatch(matchId, { status: 'declined' });
    };

    const abandonMatch = async () => {
        if (!activeMatch) return;
        const confirmed = window.confirm("Are you sure you want to forfeit and end this match? This action cannot be undone.");
        if (confirmed) {
            const { matchData, gameState, shotLog, id: matchId, isLocal } = activeMatch;
            
            const winnerHandle = currentUser.uid === matchData.player1Id ? 'player2' : 'player1';

            const finalGameState = { ...gameState, winner: winnerHandle, endTime: new Date().toISOString(), forfeited: true };
            const { historyStack, ...sanitizedGameState } = finalGameState;
            const finalMatch = { matchData, gameState: sanitizedGameState, shotLog };

            if (!isLocal) {
                await updateMatch(matchId, { gameState: sanitizedGameState, status: 'completed' });
            }
            
            const { player1Id, player2Id, player2IsProfile } = matchData;
            await saveMatch(player1Id, finalMatch);
            if (player2IsProfile) {
              await saveMatch(player2Id, finalMatch);
            }

            if (isLocal) {
                setActiveMatch(prev => ({ ...prev, gameState: finalGameState }));
                navigateTo('completed');
            }
        }
    };

    const handleUndo = async () => {
        const { gameState, shotLog, id: matchId, isLocal } = activeMatch;
        if (!gameState.historyStack || gameState.historyStack.length === 0) {
            toast.error("Nothing to undo!");
            return;
        }

        const previousHistory = [...gameState.historyStack];
        const lastStateRecord = previousHistory.pop();
        const lastShot = shotLog[shotLog.length - 1];

        const newGameState = {
            ...lastStateRecord.gameState,
            historyStack: previousHistory,
        };
        const newShotLog = lastStateRecord.shotLog;

        const updatePayload = {
            gameState: newGameState,
            shotLog: newShotLog,
        };

        if (!isLocal) {
            await updateMatch(matchId, updatePayload);
        } else {
            setActiveMatch(prev => ({ ...prev, ...updatePayload }));
        }

        if (lastShot) {
            setCurrentShotData({
                result: lastShot.result,
                tags: lastShot.tags,
                ballsMade: lastShot.ballsMade,
                distance: lastShot.distance,
                notes: lastShot.notes,
            });
        }
        
        toast.success("Last shot undone");
    };

    const handleReviewMatch = (match) => {
        setReviewingMatch(match);
        navigateTo('analytics');
    };

    const handleUpdateProfile = async (newName) => {
        if (!currentUser) return;
        await updateUserProfile(currentUser.uid, { name: newName });
        await refreshUserData();
    };

    const handleDeleteAccount = async () => {
        await fullDeleteAccount();
    };

    const handlePushOutDecision = async (takesShot) => {
        const { matchData, gameState, id: matchId, isLocal } = activeMatch;

        const decidingPlayerId = gameState.currentPlayerId;
        const originalShooterId = decidingPlayerId === matchData.player1Id ? matchData.player2Id : matchData.player1Id;

        const nextGameState = {
            ...gameState,
            isPushOutDecision: false,
            currentPlayerId: takesShot ? decidingPlayerId : originalShooterId,
            currentShotStartTime: new Date().toISOString(),
            ballInHand: false,
        };

        const { historyStack, ...sanitizedState } = nextGameState;
        
        if (!isLocal) {
            await updateMatch(matchId, { gameState: sanitizedState });
        } else {
            setActiveMatch(prev => ({...prev, gameState: nextGameState}));
        }
    };

    const requestTimeout = async () => {
        const { matchData, gameState, id: matchId, isLocal } = activeMatch;
        
        if (gameState.isTimeout) {
            toast.error("A timeout is already in progress!");
            return;
        }

        // Check if opponent is a guest (not a registered user)
        const isGuestOpponent = !matchData.player2IsProfile;
        
        const nextGameState = {
            ...gameState,
            isTimeout: true,
            timeoutRequestedBy: currentUser.uid,
            timeoutStartTime: new Date().toISOString(),
            timeoutDuration: isGuestOpponent ? 300 : 0, // Auto-start for guests, pending for registered users
        };

        const { historyStack, ...sanitizedState } = nextGameState;
        
        if (!isLocal) {
            await updateMatch(matchId, { gameState: sanitizedState });
        } else {
            setActiveMatch(prev => ({...prev, gameState: nextGameState}));
        }
        
        if (isGuestOpponent) {
            toast.success("Timeout started - 5 minutes");
        } else {
            toast.success("Timeout requested");
        }
    };

    const acceptTimeout = async () => {
        const { matchData, gameState, id: matchId, isLocal } = activeMatch;
        
        if (!gameState.isTimeout || gameState.timeoutRequestedBy === currentUser.uid) {
            toast.error("No timeout to accept!");
            return;
        }

        const nextGameState = {
            ...gameState,
            timeoutDuration: 300, // 5 minutes default
        };

        const { historyStack, ...sanitizedState } = nextGameState;
        
        if (!isLocal) {
            await updateMatch(matchId, { gameState: sanitizedState });
        } else {
            setActiveMatch(prev => ({...prev, gameState: nextGameState}));
        }
        
        toast.success("Timeout accepted - 5 minutes");
    };

    const declineTimeout = async () => {
        const { matchData, gameState, id: matchId, isLocal } = activeMatch;
        
        if (!gameState.isTimeout || gameState.timeoutRequestedBy === currentUser.uid) {
            toast.error("No timeout to decline!");
            return;
        }

        const nextGameState = {
            ...gameState,
            isTimeout: false,
            timeoutRequestedBy: null,
            timeoutStartTime: null,
            timeoutDuration: 0,
        };

        const { historyStack, ...sanitizedState } = nextGameState;
        
        if (!isLocal) {
            await updateMatch(matchId, { gameState: sanitizedState });
        } else {
            setActiveMatch(prev => ({...prev, gameState: nextGameState}));
        }
        
        toast.success("Timeout declined");
    };

    const endTimeout = async () => {
        const { matchData, gameState, id: matchId, isLocal } = activeMatch;
        
        if (!gameState.isTimeout) {
            toast.error("No timeout in progress!");
            return;
        }

        const nextGameState = {
            ...gameState,
            isTimeout: false,
            timeoutRequestedBy: null,
            timeoutStartTime: null,
            timeoutDuration: 0,
        };

        const { historyStack, ...sanitizedState } = nextGameState;
        
        if (!isLocal) {
            await updateMatch(matchId, { gameState: sanitizedState });
        } else {
            setActiveMatch(prev => ({...prev, gameState: nextGameState}));
        }
        
        toast.success("Timeout ended");
    };

    const endRack = async (isWin, lastShot) => {
        const { matchData, gameState, shotLog, id: matchId, isLocal } = activeMatch;
        const winnerOfRackId = isWin ? lastShot.playerId : (gameState.currentPlayerId === matchData.player1Id ? matchData.player2Id : matchData.player1Id);
        const winnerOfRackHandle = winnerOfRackId === matchData.player1Id ? 'player1' : 'player2';

        const scoreToUpdateKey = `${winnerOfRackHandle}Score`;
        const updatedScore = gameState[scoreToUpdateKey] + 1;

        let finalLastShot = { ...lastShot };
        const lastGameWinnerIndex = shotLog.map(s => s.isGameWinner).lastIndexOf(true);
        const currentRackShots = lastGameWinnerIndex === -1 ? shotLog : shotLog.slice(lastGameWinnerIndex + 1);
        const fullRackShots = [...currentRackShots, lastShot];
        if (isWin && fullRackShots.length > 0) {
            const playerWhoBrokeId = fullRackShots[0].playerId;
            if (playerWhoBrokeId === winnerOfRackId && fullRackShots.every(shot => shot.playerId === winnerOfRackId)) {
                finalLastShot.tags = [...new Set([...finalLastShot.tags, 'break_and_run'])];
            }
        }
        const finalShotLog = [...shotLog, { ...finalLastShot, isGameWinner: true, winnerOfRack: winnerOfRackHandle }];

        const matchIsOver = (winnerOfRackHandle === 'player1' && updatedScore >= matchData.player1RaceTo) || (winnerOfRackHandle === 'player2' && updatedScore >= matchData.player2RaceTo);

        if (matchIsOver) {
            const finalGameState = { ...gameState, [scoreToUpdateKey]: updatedScore, winner: winnerOfRackHandle, endTime: new Date().toISOString() };

            const { historyStack, ...sanitizedGameState } = finalGameState;
            const finalMatchToSave = { matchData, gameState: sanitizedGameState, shotLog: finalShotLog };

            if (!isLocal) {
                await updateMatch(matchId, { gameState: sanitizedGameState, shotLog: finalShotLog, status: 'completed' });
            }
            
            const { player1Id, player2Id, player2IsProfile } = matchData;
            await saveMatch(player1Id, finalMatchToSave);
            if (player2IsProfile) {
              await saveMatch(player2Id, finalMatchToSave);
            }

            if (isLocal) {
                setActiveMatch(prev => ({ ...prev, gameState: finalGameState, shotLog: finalShotLog }));
                navigateTo('completed');
            }

        } else {
            let nextBreakerId;
            if (matchData.whoBreaks === 'winner') {
                nextBreakerId = winnerOfRackId;
            } else {
                const firstRackBreakerId = matchData.firstBreaker === 'player1' ? matchData.player1Id : matchData.player2Id;
                const completedRacks = gameState.player1Score + gameState.player2Score + 1;
                nextBreakerId = (completedRacks % 2 === 0) ? firstRackBreakerId : (firstRackBreakerId === matchData.player1Id ? matchData.player2Id : matchData.player1Id);
            }

            const nextGameState = {
                ...gameState,
                [scoreToUpdateKey]: updatedScore,
                currentInning: 1,
                currentShot: 1,
                consecutiveFouls: { [matchData.player1Id]: 0, [matchData.player2Id]: 0 },
                currentPlayerId: nextBreakerId,
                currentShotStartTime: new Date().toISOString(),
                ballsRemaining: getAvailableBalls(matchData.gameType),
                historyStack: [],
            };

            const { historyStack, ...sanitizedNextGameState } = nextGameState;

            if (!isLocal) {
                await updateMatch(matchId, { gameState: sanitizedNextGameState, shotLog: finalShotLog });
            } else {
                setActiveMatch(prev => ({ ...prev, gameState: nextGameState, shotLog: finalShotLog }));
            }
        }
    };

    const logShot = async (shotData) => {
        if (activeMatch.gameState.winner) {
            toast.error("Match is already over.");
            return;
        }

        const { matchData, gameState, shotLog, id: matchId, isLocal } = activeMatch;
        const { ballsRemaining, currentPlayerId } = gameState;
        const { historyStack: _, ...sanitizedCurrentState } = gameState;
        const historyStack = gameState.historyStack || [];
        const newHistory = [...historyStack, { gameState: sanitizedCurrentState, shotLog }];

        const shotEndTime = new Date();
        const shotStartTime = gameState.currentShotStartTime || shotEndTime.toISOString();
        const shotDuration = Math.round((shotEndTime - new Date(shotStartTime)) / 1000);

        const lastGameWinnerIndex = shotLog.map(s => s.isGameWinner).lastIndexOf(true);
        const currentGameShots = lastGameWinnerIndex === -1 ? shotLog : shotLog.slice(lastGameWinnerIndex + 1);
        const isBreakShot = currentGameShots.length === 0;

        let potentialTags = [...shotData.tags];
        if (isBreakShot) potentialTags.push('break');
        if (gameState.ballInHand) potentialTags.push('ball_in_hand');
        
        const winningBall = getWinningBall(matchData.gameType);
        if (ballsRemaining.length === 1 && ballsRemaining[0] === winningBall) {
            potentialTags.push('money_ball_shot');
        }
        
        // Check for special shots (golden break, etc.)
        const specialTags = checkSpecialShots(matchData.gameType, shotData, matchData, isBreakShot);
        potentialTags.push(...specialTags);
        
        const finalTags = [...new Set(potentialTags)];

        let shotRecord = {
            id: Date.now(), inning: gameState.currentInning, shotNumber: gameState.currentShot,
            playerId: currentPlayerId, player: currentPlayerId === matchData.player1Id ? 'player1' : 'player2',
            result: shotData.result, tags: finalTags,
            ballsMade: shotData.ballsMade, distance: shotData.distance, notes: shotData.notes,
            timestamp: shotEndTime.toISOString(), duration: shotDuration,
            isGameWinner: false,
        };

        let ballsToSpot = [];
        
        const triggersDecision = 
            (shotData.result === 'push_out') ||
            (matchData.gameType === '10-Ball' && shotData.result === 'miss' && shotData.ballsMade.length > 0);

        if (triggersDecision) {
            if (shotData.ballsMade.includes(winningBall)) {
                ballsToSpot.push(winningBall);
            }

            const opponentId = currentPlayerId === matchData.player1Id ? matchData.player2Id : matchData.player1Id;
            const nextGameState = {
                ...gameState,
                isPushOutDecision: true,
                currentPlayerId: opponentId,
                ballsRemaining: ballsRemaining.filter(b => !shotData.ballsMade.includes(b)).concat(ballsToSpot).sort((a,b) => a-b),
                ballInHand: false,
                currentShotStartTime: shotEndTime.toISOString(),
                historyStack: newHistory,
            };
            
            const newShotLog = [...shotLog, shotRecord];
            const { historyStack, ...sanitizedState } = nextGameState;

            if (!isLocal) {
                await updateMatch(matchId, { gameState: sanitizedState, shotLog: newShotLog });
            } else {
                setActiveMatch(prev => ({...prev, gameState: nextGameState, shotLog: newShotLog}));
            }
            return;
        }
        
        if (shotData.result === 'foul' && matchData.threeFoulRule && (gameState.consecutiveFouls[currentPlayerId] || 0) + 1 >= 3) {
            endRack(false, shotRecord); 
            return;
        }

        if (matchData.gameMode === 'ghost' && ['miss', 'foul', 'safety'].includes(shotData.result)) {
            endRack(false, shotRecord); return;
        }
        
        let rackOver = false;
        let { isWin, ballsToSpot: winBallsToSpot } = checkWinCondition(
            matchData.gameType, 
            shotData, 
            matchData, 
            isBreakShot, 
            finalTags
        );
        
        ballsToSpot.push(...winBallsToSpot);
        
        // Check for 8-ball lose condition
        if (matchData.gameType === '8-Ball' && 
            shotData.ballsMade.includes(8) && 
            finalTags.includes('lose')) {
            // 8-ball was made with "Lose" tag - opponent wins the rack
            rackOver = true;
            isWin = false; // Current player loses, opponent wins
            shotRecord.tags = [...shotRecord.tags, 'lose'];
            shotRecord.isGameWinner = true; // This shot ends the rack
        }
        
        if (isWin && shotData.result === 'foul') { 
            isWin = false; 
            if (shotData.ballsMade.includes(winningBall)) { 
                ballsToSpot.push(winningBall);
            } 
        }
        
        if (isWin) { 
            rackOver = true; 
            // Add win tag to the shot record
            shotRecord.tags = [...shotRecord.tags, 'win'];
            shotRecord.isGameWinner = true;
        }
        
        if (rackOver) {
            endRack(isWin, shotRecord);
            return;
        }

        let nextGameState = { ...gameState };
        const turnEnds = ['miss', 'foul', 'safety', 'dry_break'].includes(shotData.result) || (shotData.result === 'made' && shotData.ballsMade.length === 0);
        
        if (turnEnds) {
            nextGameState.currentPlayerId = currentPlayerId === matchData.player1Id ? matchData.player2Id : matchData.player1Id;
            nextGameState.currentShot = 1;
            if (shotLog.length > 0) {
                 const otherPlayerHasShotInInning = shotLog.some(s => s.inning === gameState.currentInning && s.playerId !== currentPlayerId);
                 if (otherPlayerHasShotInInning || isBreakShot) {
                    nextGameState.currentInning++;
                 }
            } else if (isBreakShot) {
                nextGameState.currentInning++;
            }
        } else {
            nextGameState.currentShot++;
        }

        nextGameState.ballInHand = shotData.result === 'foul';
        nextGameState.ballsRemaining = ballsRemaining.filter(b => !shotData.ballsMade.includes(b)).concat(ballsToSpot).sort((a,b) => a-b);
        nextGameState.currentShotStartTime = shotEndTime.toISOString();
        nextGameState.historyStack = newHistory;
        
        let nextFouls = { ...gameState.consecutiveFouls };
        if (shotData.result === 'foul') {
            nextFouls[currentPlayerId] = (nextFouls[currentPlayerId] || 0) + 1;
        } else {
            nextFouls[currentPlayerId] = 0;
        }
        nextGameState.consecutiveFouls = nextFouls;
        
        const finalShotLog = [...shotLog, shotRecord];
        
        const { historyStack: __, ...sanitizedUpdateState } = nextGameState;
        const updatePayload = { shotLog: finalShotLog, gameState: sanitizedUpdateState };
        
        if (!isLocal) {
            await updateMatch(matchId, updatePayload);
        } else {
            setActiveMatch(prev => ({...prev, shotLog: finalShotLog, gameState: nextGameState}));
        }
    };

    const recordScore = async (winnerHandle, delta = 1) => {
        const { matchData, gameState, id: matchId, isLocal } = activeMatch;
        const scoreKey = `${winnerHandle}Score`;
        const raceKey = `${winnerHandle}RaceTo`;

        let updatedScore = gameState[scoreKey] + delta;
        if (updatedScore < 0) updatedScore = 0;

        let nextGameState = {
            ...gameState,
            [scoreKey]: updatedScore,
        };

        if (!gameState.startTime && delta > 0) {
            nextGameState.startTime = new Date().toISOString();
        }

        const matchOver = delta > 0 && updatedScore >= matchData[raceKey];

        if (matchOver) {
            nextGameState = { ...nextGameState, winner: winnerHandle, endTime: new Date().toISOString() };
        }

        const { historyStack, ...sanitizedState } = nextGameState;

        if (!isLocal) {
            const updateData = matchOver
                ? { gameState: sanitizedState, status: 'completed' }
                : { gameState: sanitizedState };
            await updateMatch(matchId, updateData);

            if (matchOver) {
                const finalMatch = { matchData, gameState: sanitizedState, shotLog: [] };
                await saveMatch(matchData.player1Id, finalMatch);
                if (matchData.player2IsProfile) {
                    await saveMatch(matchData.player2Id, finalMatch);
                }
            }
        } else {
            setActiveMatch(prev => ({ ...prev, gameState: nextGameState }));

            if (matchOver) {
                const finalMatch = { matchData, gameState: sanitizedState, shotLog: [] };
                await saveMatch(matchData.player1Id, finalMatch);
                if (matchData.player2IsProfile) {
                    await saveMatch(matchData.player2Id, finalMatch);
                }
                navigateTo('completed');
            }
        }
    };
    
    const isMatchInProgress = !!activeMatch && !activeMatch.gameState.winner;

    if (initialLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p className="text-xl text-slate-400">Loading...</p>
            </div>
        );
    }

    return (
        <>
            <ChallengeNotificationModal 
                invite={invites.length > 0 ? invites[0] : null}
                onAccept={handleAcceptChallenge}
                onDecline={handleDeclineChallenge}
            />
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentView}
                    initial={{ opacity: 0.8 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0.8 }}
                    transition={{ duration: 0.2 }}
                >
                    {(() => {
                        switch (currentView) {
                            case 'setup':
                                return <SetupView 
                                            matchData={setupMatchData} 
                                            setMatchData={setSetupMatchData} 
                                            gameMode={setupMatchData.gameMode}
                                            handleSendChallenge={handleSendChallenge}
                                            onBack={() => navigateTo('home')}
                                            allProfiles={allProfiles} 
                                            loadingProfiles={loading}
                                            opponentProfile={opponentProfile}
                                            setOpponentProfile={setOpponentProfile}
                                            guestOpponentName={guestOpponentName}
                                            setGuestOpponentName={setGuestOpponentName}
                                            controlModeDisabled={!opponentProfile}
                                        />;
                            case 'tracking':
                                if (!activeMatch) return <div className="p-4 text-center">Loading Match...</div>;
                                if (activeMatch.matchData.trackingMode === 'simple') {
                                    return <SimpleTrackingView
                                                matchData={activeMatch.matchData}
                                                gameState={activeMatch.gameState}
                                                recordScore={recordScore}
                                                abandonMatch={abandonMatch}
                                            />;
                                }
                                return <TrackingView
                                            currentUser={currentUser}
                                            matchData={activeMatch.matchData}
                                            gameState={activeMatch.gameState}
                                            shotLog={activeMatch.shotLog}
                                            currentShotData={currentShotData}
                                            setCurrentShotData={setCurrentShotData}
                                            logShot={logShot}
                                            handleUndo={handleUndo}
                                            handlePushOutDecision={handlePushOutDecision}
                                            navigateTo={navigateTo}
                                            abandonMatch={abandonMatch}
                                            requestTimeout={requestTimeout}
                                            acceptTimeout={acceptTimeout}
                                            declineTimeout={declineTimeout}
                                            endTimeout={endTimeout}
                                        />;
                            case 'completed':
                                if (!activeMatch) return <div className="p-4 text-center">Loading Results...</div>;
                                return <CompletedView 
                                            matchData={activeMatch.matchData} 
                                            gameState={activeMatch.gameState} 
                                            shotLog={activeMatch.shotLog}
                                            gameMode={activeMatch.matchData.gameMode}
                                            startNewMatch={startNewMatch} 
                                            navigateTo={navigateTo} 
                                            returnHome={() => {
                                                setActiveMatch(null);
                                                setReviewingMatch(null);
                                                navigateTo('home');
                                            }} 
                                        />;
                            case 'analytics':
                                const dataForAnalytics = reviewingMatch || activeMatch;
                                if (!dataForAnalytics) return <div className="p-4 text-center">No analytics data found.</div>;
                                return <AnalyticsView
                                            matchData={dataForAnalytics.matchData}
                                            shotLog={dataForAnalytics.shotLog}
                                            gameState={dataForAnalytics.gameState}
                                            isReviewing={!!reviewingMatch}
                                            onBack={() => {
                                                setReviewingMatch(null);
                                                navigateTo(previousView);
                                            }}
                                            abandonMatch={abandonMatch}
                                            returnHome={() => {
                                                setActiveMatch(null);
                                                setReviewingMatch(null);
                                                navigateTo('home');
                                            }}
                                        />;
                            case 'stats':
                                return <StatsView 
                                            matchHistory={matchHistory} 
                                            currentUserData={currentUserData} 
                                            allProfiles={allProfiles}
                                            onBack={() => navigateTo('home')}
                                            onReviewMatch={handleReviewMatch}
                                        />;
                            case 'profile':
                                return <ProfileView 
                                    currentUserData={currentUserData}
                                    onBack={() => navigateTo('home')}
                                    onLogout={logout}
                                    onUpdateProfile={handleUpdateProfile}
                                    onDeleteAccount={handleDeleteAccount}
                                />;
                            case 'waiting':
                                return <WaitingView 
                                    matchData={activeMatch?.matchData}
                                    onCancelChallenge={handleCancelChallenge}
                                />;
                             case 'practice':
                                return <PracticeView 
                                            matchHistory={matchHistory}
                                            currentUserData={currentUserData}
                                            onBack={() => navigateTo('home')}
                                            onReviewMatch={handleReviewMatch}
                                        />;
                            case 'drill':
                                return <DrillView 
                                            currentUserData={currentUserData}
                                            onBack={() => navigateTo('home')}
                                        />;
                            case 'home':
                            default:
                                return <HomeView 
                                            currentUserData={currentUserData} 
                                            onStartNewMatch={startNewMatch}
                                            onLogout={logout} 
                                            matchHistory={matchHistory} 
                                            loadingHistory={loading}
                                            invites={invites}
                                            onViewStats={() => navigateTo('stats')}
                                            onViewPractice={() => navigateTo('practice')}
                                            onViewDrill={() => navigateTo('drill')}
                                            onReviewMatch={handleReviewMatch}
                                            isMatchInProgress={isMatchInProgress}
                                            onResumeMatch={() => navigateTo('tracking')}
                                            onNavigateToProfile={() => navigateTo('profile')}
                                            onAcceptChallenge={handleAcceptChallenge}
                                            onDeclineChallenge={handleDeclineChallenge}
                                        />;
                        }
                    })()}
                </motion.div>
            </AnimatePresence>
        </>
    );
};